import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { IndianRupee, Search, Download } from "lucide-react"
import { Input } from "../components/ui/input.tsx"
import { Button } from "../components/ui/button.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import api from "../utils/api"

// Helper function to convert string to Title Case
function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function BillsReceiptsPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [dateFilter, setDateFilter] = useState("")
  const [bills, setBills] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBills = async () => {
      try {
        const userStr = localStorage.getItem("user")
        if (!userStr) {
          setLoading(false)
          return
        }
        
        const user = JSON.parse(userStr)
        let mobile = user?.mobile || user?.phone || ""
        if (!mobile) {
          setLoading(false)
          return
        }

        if (!mobile.startsWith("91")) {
          mobile = `91${mobile}`
        }

        const response = await api.get(`/apis/web/receipts?mobile=${mobile}`)
        
        if (response.data?.result && Array.isArray(response.data.result)) {
          const mapped = response.data.result.map((item, index) => {
            const trnDate = item.TRNDATE ? new Date(item.TRNDATE) : null;
            return {
              id: item.RECEIPT_NO || item.BILL_NO || index + 1,
              receiptType: toTitleCase(item.RECEIPT_TYPE || item.PARTICULARS || "Medical Service"),
              patient: toTitleCase((item.PATIENT_NAME || '').trim()),
              age: item.Age || '',
              gender: item.Gender === 'M' ? 'Male' : item.Gender === 'F' ? 'Female' : item.Gender || '',
              mrno: item.MRNO || '',
              doctor: toTitleCase((item.DOCTOR || '').trim()),
              date: trnDate ? trnDate.toLocaleDateString('en-GB') : (item.RECEIPT_DATE || item.BILL_DATE || ""),
              isoDate: trnDate ? trnDate.toISOString().split('T')[0] : "",
              downloadUrl: item.WHATSAPP_URL || ""
            };
          })
          setBills(mapped)
        }
      } catch (error) {
        console.error("Error fetching bills:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchBills()
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const filteredBills = bills.filter(bill => 
    (bill.receiptType.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bill.patient.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bill.doctor.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bill.mrno.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bill.date.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (dateFilter === "" || bill.isoDate === dateFilter)
  )

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <IndianRupee className="w-6 h-6 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900">Bills & Receipts</h1>
          </div>
        </div>

        <div className="px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="relative flex-1 w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="relative">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full sm:w-auto"
                  />
                </div>
                {dateFilter && (
                  <Button variant="ghost" onClick={() => setDateFilter("")} className="text-xs">
                    Clear Date
                  </Button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-gray-700">
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Receipt Type</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Patient</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Age</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Gender</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">MR No</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Doctor</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium whitespace-nowrap">Date</th>
                    <th className="px-4 md:px-6 py-3 text-center text-xs md:text-sm font-medium whitespace-nowrap">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                          <p className="text-gray-500">Loading documents...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredBills.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center text-gray-500 font-medium">
                        {searchQuery 
                          ? `No documents found matching "${searchQuery}"` 
                          : "No bills or receipts available."}
                      </td>
                    </tr>
                  ) : (
                    filteredBills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-900 font-medium whitespace-nowrap">{bill.receiptType}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-900 whitespace-nowrap">{bill.patient}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 whitespace-nowrap">{bill.age}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 whitespace-nowrap">{bill.gender}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 whitespace-nowrap font-mono">{bill.mrno}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 whitespace-nowrap">{bill.doctor}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 whitespace-nowrap">{bill.date}</td>
                      <td className="px-4 md:px-6 py-4 text-center">
                        {bill.downloadUrl ? (
                          <a 
                            href={bill.downloadUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-red-600 hover:text-red-800 transition-colors inline-block"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        ) : (
                          <span className="text-gray-300">
                            <Download className="w-5 h-5" />
                          </span>
                        )}
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}
